import { useCallback } from 'react';


export type ImageType = 'banner' | 'avatar' | 'nft' | 'custom';

interface UseImageErrorReturn {
    onError: (e: React.SyntheticEvent<HTMLImageElement>, additionalData?: any) => void;
    fallbackUrl: string;
}

/**
 * Hook for handling image errors with fallback images
 *
 * @param type - Type of image: 'banner', 'avatar', 'nft', or 'custom'
 * @param customFallback - Custom fallback URL for 'custom' type
 * @returns Event handler and fallback URL
 */
export const useImageError = (type: ImageType = 'nft', customFallback: string | null = null): UseImageErrorReturn => {
    const fallbackImages: Record<Exclude<ImageType, 'custom'>, string> = {
        banner: 'https://cdn.stealthisnft.xyz/u/0/profile/banner.jpg',
        avatar: 'https://cdn.stealthisnft.xyz/u/0/profile/avatar.jpg',
        nft: 'https://cdn.stealthisnft.xyz/placeholder-nft.jpg',
    };

    const getFallbackUrl = useCallback(() => {
        if (type === 'custom' && customFallback) {
            return customFallback;
        }
        return fallbackImages[type as Exclude<ImageType, 'custom'>] || fallbackImages.nft;
    }, [type, customFallback]);

    const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>, additionalData: any = null) => {
        const target = e.target as HTMLImageElement;
        target.onError = null;
        const fallback = getFallbackUrl();

        // For images with background-image style
        if (target.style && type === 'banner') {
            target.style.backgroundImage = `url(${fallback})`;
            if (additionalData?.id) {
                // Could integrate with failedImages state if passed
            }
        } else {
            target.src = fallback;
        }
    }, [type, customFallback, getFallbackUrl]);

    return {
        onError: handleError,
        fallbackUrl: getFallbackUrl(),
    };
};

export default useImageError;
